import {
  TeButton,
  TeContainer,
  TeContainerSlim,
  TeLabel,
  TeMultiselect,
  TeMultiselectOption,
  TeText,
} from '@topcon/tuicl-react';
import { TeMultiselectChangeEvent } from '@topcon/tuicl';
import { RightDrawer } from '@topcon/topcon-utils';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DivisionSummary } from '../../../hooks/queries/useDivisionRollup';

export interface DivisionFilterDrawerProps {
  open: boolean;
  onClose: () => void;
  divisions: DivisionSummary[];
  selectedDivisionIds: string[];
  onApply: (selectedIds: string[]) => void;
  onClear: () => void;
}

/**
 * Division filter drawer shared by Customers (02) and Invites (03): a single
 * Division multiselect, with Clear / Apply pinned to the drawer's bottom edge,
 * matching the Figma (node 6156:23620). The other drawer fields in the Figma
 * (Service type, Company name, Customer number, Active since, Organization
 * number, Country) are out of scope (PRD D2, D14, ticket 06).
 */
export default function DivisionFilterDrawer({
  open,
  onClose,
  divisions,
  selectedDivisionIds,
  onApply,
  onClear,
}: DivisionFilterDrawerProps) {
  const { t } = useTranslation();
  const [pendingIds, setPendingIds] = useState(selectedDivisionIds);

  // Re-seed the pending selection from the applied filter each time the
  // drawer opens, so a close-without-applying doesn't leak into next time.
  useEffect(() => {
    if (open) setPendingIds(selectedDivisionIds);
  }, [open, selectedDivisionIds]);

  const handleClear = () => {
    setPendingIds([]);
    onClear();
    onClose();
  };

  const handleApply = () => {
    onApply(pendingIds);
    onClose();
  };

  return (
    <RightDrawer open={open}>
      <TeContainer shadow="bottom" gap="xs2" column fill>
        <TeText size={18} weight={600}>
          {t('customersTable.filterDrawerTitle')}
        </TeText>
        <TeText color="text.secondary" size={13}>
          {t('customersTable.filterDrawerSubtitle')}
        </TeText>
      </TeContainer>
      <TeContainer column gap="xs" grow fill overflow="auto">
        <TeContainerSlim column gap="xs" fill>
          <TeLabel text={t('customersTable.filterDrawerDivisionLabel')} />
          <TeMultiselect
            size="s"
            fixedPosition
            placeholder={t('customersTable.filterDrawerDivisionPlaceholder')}
            techange={(event: TeMultiselectChangeEvent) => setPendingIds(event.values)}
          >
            {divisions.map((division) => (
              <TeMultiselectOption
                key={division.id}
                text={division.name}
                value={division.id}
                selected={pendingIds.includes(division.id)}
              />
            ))}
          </TeMultiselect>
        </TeContainerSlim>
      </TeContainer>
      <TeContainer shadow="top" gap="xs" padding="xs" fill>
        <TeButton
          variant="neutral"
          size="s"
          grow
          text={t('customersTable.filterDrawerClear')}
          teclick={handleClear}
        />
        <TeButton
          variant="secondary"
          size="s"
          grow
          text={t('customersTable.filterDrawerApply')}
          teclick={handleApply}
        />
      </TeContainer>
    </RightDrawer>
  );
}
